import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import compact from 'lodash/compact';
import { useStaticQuery, graphql, Link } from 'gatsby';
import ReactSelect, { components } from 'react-select';
import { useIntl } from 'react-intl';

import dropdownIndicator from '../../MOVEMETOCDN/dropdown-indicator.svg';
import globe from '../../MOVEMETOCDN/globe.svg';
import NavigationBrand from '../NavigationBrand/NavigationBrand';
import TopBarCTA from '../TopBarCTA/TopBarCTA';
import { NavigationItem } from '../NavigationItem/NavigationItem';
import { customStyles, productListStyles } from './react-select-styles';
import { WebsiteLocaleFragment } from '../../../graphql.schema';
import { GlobalPageContext } from '../../types';
import bcp47LanguageCodes from './bcp47LanguageCode.json';
import localeDropDownArrow from '../../MOVEMETOCDN/web-arrow-white.svg';
import { v4tov3base } from '../../lib/mapV4toV3';

import * as styles from './Header.module.scss';

type HeaderProps = {
  websiteLocale: WebsiteLocaleFragment;
  isInternationalSite?: boolean;
  pathPrefixes: GlobalPageContext['pathPrefixes'];
  id: string;
  availableRoutes: any
};

type WebsiteLocaleTypes = {
  documentId: string;
  website_locales: Array<{
    language_code: string;
    is_default: boolean
  }>;
  default_website_locale: {
    language_code: string;
  };
};
const Header: React.FC<HeaderProps> = ({ websiteLocale, isInternationalSite = false, pathPrefixes, id, availableRoutes }) => {
  const learningProducts = websiteLocale?.learningProducts;
  const [isOpen, setIsOpen] = useState(false);
  const [isShowContainer, setContainerStatus] = useState(false);
  const intl = useIntl();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const {
    site: { siteMetadata },
    strapi: v4Strapi,
  } = useStaticQuery(graphql`
    query StaticHeaderData {
      site {
        siteMetadata {
          title
          studentLogin
        }
      }
      strapi {
        websites(pagination: { limit: 100}) {
          data {
            documentId
            attributes {
              website_locales {
                data {
                  documentId
                  attributes {
                    learningProducts: learning_products {
                      productName: product_name
                      productLinkUrl: product_link_url
                      productImage: product_image {
                        data {
                          documentId
                          attributes {
                            url
                          }
                        }
                      }
                    }
                    language_code
                     is_default
                    localizations{
                      data{
                      attributes{
                       locale
                       is_default
                      }
                     }
                     }
                  }
                }
              }
              default_website_locale {
                data {
                  attributes {
                    language_code
                    is_default
                    localizations{
                      data{
                      attributes{
                       locale
                        is_default
                      }
                     }
                     }
                  }
                }
              }
            }
          }
        }
        homepages(pagination: { limit: 100 }){
          data {
            attributes {
              locale
              publishedAt
              website_locale {
                data {
                  documentId
                }
              }
            }
          }
        }
      }
    }
  `);

  const strapi = v4tov3base(v4Strapi);
  const websiteLocaleToV3 = {
    id: websiteLocale.documentId,
    // @ts-ignore
    ...v4tov3base(websiteLocale),
  };
  const websites = strapi.websites;
  const homepages = strapi.homepages;
  const navigationItems = compact(websiteLocaleToV3.navigation || []);
  const mapHeaderSiteLinks = (siteLinks: WebsiteLocaleFragment['internationalHeaderSiteLinks'], region: string) => {
    return compact(
      compact(siteLinks)
        .filter((link) => link.region === region)
        .map(({ linkUrl, territory, flagImage }) => {
          if (linkUrl && territory && flagImage) {
            return {
              value: territory,
              label: `
                <a class="${styles.locationDropdownLabel}" href="${linkUrl}">
                  <img
                    class="${styles.locationDropdownIcon}"
                    src="${flagImage?.data.attributes.url}"
                    alt="${intl.formatMessage({ id: 'flagOfCountry' }, { country: territory })}"
                    loading="lazy"
                  />
                  ${territory}
                </a>`,
            };
          }
          return null;
        }),
    );
  };

  const groupedOptions = [
    {
      label: 'Asia Pacific',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'asia_pacific'),
    },
    {
      label: 'Europe, middle-east & africa',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'europe_africa_and_middle_east'),
    },
    {
      label: 'Latin America',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'latin_america'),
    },
  ];

  const Options = learningProducts.map(({ productName, productLinkUrl, productImage }) => {
    let dropdownLabel;

    if (productImage?.data == null || undefined) {
      dropdownLabel = `
      <span class="${styles.productDropdownContent}">${productName}</span>`;
    } else {
      dropdownLabel = `<img
        class="${styles.productDropdownIcon}"
        src="${productImage?.data?.attributes?.url}"
        alt="${productName}"
        loading="lazy"
      />`;
    }

    return {
      label: ` 
        <a class="${styles.locationDropdownLabel}" href="${productLinkUrl}" target="_blank">
          ${dropdownLabel}
        </a>`,
    };
  });

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <img src={dropdownIndicator} />
      </components.DropdownIndicator>
    );
  };

  const Placeholder = (props: any) => {
    return (
      <components.Placeholder {...props}>
        <div className="is-flex is-align-items-center">
          <img src={globe} alt="A Globe icon" />
          <span className="ml-2">Global</span>
        </div>
      </components.Placeholder>
    );
  };

  const LocalesDropDown: JSX.Element | null = useMemo(() => {
    const locales: WebsiteLocaleTypes = websites.find((e: WebsiteLocaleTypes) => e.documentId === id);
    if (!locales) {
      return null;
    }

    const availableWebsiteLocales = locales.website_locales.reduce((accumulator, e) => {
      const availableHomepages = homepages.filter((item) => item.website_locale.documentId === e.documentId && item.publishedAt != null);
      const [language, country] = e.language_code.split('-');
      const languageName = bcp47LanguageCodes[language]?.nativeName;
      const route = compact(availableRoutes).find((x) => x?.locale == e.language_code ? x?.route : null);
      const mainRoute = `${global.window?.origin ?? ''}/${route?.route}`;
      if (availableHomepages.length > 0) {
        const localeData = {
          value: language.toUpperCase(),
          label: languageName ? languageName : '',
          locale: e.language_code,
          redirectUri: e.is_default ? mainRoute.replace(new RegExp(`/${language}`), '') : mainRoute,
          route: route?.route
        };
        accumulator.push(localeData);
      }
      return accumulator;
    }, [])

    return availableWebsiteLocales.length > 1 ? (
      <div className={classnames(styles.languageDropdown)} onClick={() => setContainerStatus(!isShowContainer)}>
        <div className={classnames("is-flex", websiteLocale.hasRightToLeftLanguage ? styles.languageCode : '')}>
          {availableWebsiteLocales.find((e) => e.locale === websiteLocale.languageCode)?.label ?? ''}
          <img src={localeDropDownArrow} className={styles.invertImage} />
        </div>
        <div
          className={classnames(
            styles.languageContent,
            isShowContainer ? styles.languageContentDisplay : styles.languageContentHide,
          )}
        >
          {availableWebsiteLocales
            .filter((e) => e.locale !== websiteLocale.languageCode)
            .map((data) => (
              <div key={data.value}>
                <div className={classnames(data.route ? '' : styles.wrapper)}>
                  <Link to={data.route ? data.redirectUri : ''} key={data.value} className={classnames('is-flex', styles.languageOption, data.route ? styles.routeAvailable : styles.routeNotAvailable)}>
                    <p >{data.label}</p>
                  </Link>
                </div>
              </div>
            ))
            }
        </div>
      </div>
    ) : null;
  }, [websiteLocale, isShowContainer]);
  return (

    <header className={classnames(styles.root)}>
      <div className={classnames('has-bg-brand-a py-3', styles.overhead)}>
        <div className={classnames('container', styles.overheadInner)}>
          {isInternationalSite ? (
            <div data-testid='select-country' className={styles.locationDropdown}>
              <p className={classnames(styles.locationHelperText, 'is-hidden-mobile')}>Choose a region</p>
              <div className="is-flex py-2 px-4">
                {hasMounted ? (
                  <ReactSelect
                    styles={customStyles}
                    options={groupedOptions}
                    instanceId="territory-dropdown"
                    value={undefined}
                    components={{ DropdownIndicator, IndicatorSeparator: () => null, Placeholder }}
                    onChange={() => null}
                    hideSelectedOptions={false}
                    isSearchable={false}
                    formatOptionLabel={(data) => <span dangerouslySetInnerHTML={{ __html: data.label }}></span>}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className={classnames(styles.topBarLinks)}>
            <TopBarCTA websiteLocale={websiteLocale} cta={websiteLocaleToV3.topBarCta} />
            {websiteLocaleToV3.telephone ? (
              <a
                className={classnames(styles.telephoneLink, 'is-hidden-mobile')}
                href={`tel:${websiteLocaleToV3.telephone}`}
                data-testid='telephone'
              >
                {websiteLocaleToV3.telephone}
              </a>
            ) : null}
            {!isInternationalSite && <div className="is-hidden-mobile is-flex pl-3" data-testid='localedropdown'>{LocalesDropDown}</div>}
            {(id === 'o1vsas7qxp9yt1yx5o1maxw2' || id === 'frv0glxlt30v75jyrrbg28q5') ?
              <div className="is-flex py-2 px-4" data-testid='login-dropdown'>
                {hasMounted ? (
                  <ReactSelect
                    styles={productListStyles}
                    instanceId="login-dropdown"
                    options={Options}
                    value={undefined}
                    onChange={() => null}
                    hideSelectedOptions={false}
                    isSearchable={false}
                    formatOptionLabel={(data) => <span dangerouslySetInnerHTML={{ __html: data.label }}></span>}
                    placeholder={intl.formatMessage({ id: 'login-text', defaultMessage: 'Login' })}
                    controlShouldRenderValue={false}
                  />
                ) : null}
              </div>
              :
              <a href={siteMetadata.studentLogin} target="_blank" className={classnames('link pl-3', styles.studentLoginLink)} rel="nofollow" data-testid='student-login'>
                {intl.formatMessage({ id: 'login-text', defaultMessage: 'Student Login' })}
              </a>
            }
          </div>
        </div>
      </div>
      <div className="container">
        <nav
          className={classnames(styles.navigation, 'navigation navbar is-white')}
          role="navigation"
          aria-label="main navigation"
        >
          <NavigationBrand
            isOpen={isOpen}
            onMenuToggle={setIsOpen}
            pathPrefixes={pathPrefixes}
            telephoneNumber={websiteLocaleToV3.telephone ?? ''}
            LocalesDropDown={LocalesDropDown}
          />

          <div className={classnames('navbar-menu', { 'is-active': isOpen })}>
            <div className="navbar-end mr-3">
              {hasMounted && navigationItems.map((item, idx) => {
                if (!item) return null;
                return (
                  <NavigationItem
                    data-testid='nav-dropdown'
                    key={`header-navitem-${idx}`}
                    item={item}
                    className={classnames({ 'is-right': idx === navigationItems.length - 1 })}
                  />
                );
              })}
              {websiteLocaleToV3.externalBlogText && websiteLocaleToV3.externalBlogUrl && (
                <a
                  className="navbar-item is-text-1"
                  target="_blank"
                  rel="noreferrer"
                  href={websiteLocaleToV3.externalBlogUrl}
                  data-testid='navbar-item'
                >
                  {websiteLocaleToV3.externalBlogText}
                </a>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;